import { setRedirectRoute } from 'router/redirect'

import config from 'common/config'
import Constants from 'common/constants'
// import AuthModule from 'common/auth'
//import { setRedirectRoute } from 'router/redirect'
//import i18n from 'common/i18n'
import { toastService } from 'common/services/toast/service'
import { getTenantSlug } from 'common/tenant'
import { tenantPath } from 'common/tenant/url'

const getToken = (): string => {
  try {
    const token = localStorage.getItem(Constants.TOKEN_LOCAL_STORAGE) || ''
    return token !== '' ? `Bearer ${token}` : ''
  } catch {
    return ''
  }
}

const getRefreshToken = (): string => {
  try {
    const auth = localStorage.getItem(Constants.AUTH_LOCAL_STORAGE)
    if (!auth) {
      return ''
    }
    return JSON.parse(auth)?.refreshToken || ''
  } catch {
    return ''
  }
}

const persistTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(
    Constants.AUTH_LOCAL_STORAGE,
    JSON.stringify({ accessToken, refreshToken })
  )
  localStorage.setItem(Constants.TOKEN_LOCAL_STORAGE, accessToken)
}

let logoutEmAndamento = false

const clearToken = () => {
  if (logoutEmAndamento) {
    return
  }
  logoutEmAndamento = true
  localStorage.removeItem(Constants.AUTH_LOCAL_STORAGE)
  localStorage.removeItem(Constants.TOKEN_LOCAL_STORAGE)
  setRedirectRoute()
  // Sessao expirada nao pode custar a imobiliaria: mandar para `/login` cru
  // devolvia o usuario ao login da raiz e, dali em diante, ao site da
  // imobiliaria original.
  window.location.href = tenantPath('/login')
}

let refreshEmAndamento: Promise<boolean> | null = null

// Chamada crua ao endpoint de refresh (nao usa AuthService por causa do
// import circular: services/auth importa este arquivo).
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return false
  }
  try {
    const response = await fetch(
      `${config.API_URL}/${Constants.API_URl_PREFIX}/auth/refresh`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      }
    )
    if (!response.ok) {
      return false
    }
    const payload = await response.json()
    if (!payload?.accessToken) {
      return false
    }
    persistTokens(payload.accessToken, payload.refreshToken || refreshToken)
    return true
  } catch {
    return false
  }
}

// Deduplica renovacoes concorrentes: todas as chamadas que expiram juntas
// aguardam a mesma renovacao em andamento em vez de disparar uma por 401.
function tryRefreshToken(): Promise<boolean> {
  if (!refreshEmAndamento) {
    refreshEmAndamento = refreshAccessToken().finally(() => {
      refreshEmAndamento = null
    })
  }
  return refreshEmAndamento
}

// Cabecalhos de autenticacao para conexoes que nao passam pelo `fetchData`,
// como o stream SSE do painel de atividade.
export function getAuthHeaders(): Record<string, string> {
  const token = getToken()
  const tenantSlug = getTenantSlug()
  return {
    ...(token ? { Authorization: token } : {}),
    ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {})
  }
}

export { tryRefreshToken }

async function fetchData<T>(
  basePath: string,
  options?: RequestInit,
  muteException?: boolean,
  skipContentType?: boolean,
  isRetry?: boolean
): Promise<T> {
  try {
    const withCredentials = options?.credentials !== 'omit'
    const token = getToken()
    // Identifica a imobiliaria nas requisicoes publicas. Em requisicao
    // autenticada o back-end usa o tenant do proprio token e ignora o header.
    const tenantSlug = getTenantSlug()
    const baseHeaders: Record<string, string> = {
      ...(skipContentType ? {} : { 'Content-Type': 'application/json' }),
      ...(withCredentials ? { Authorization: token } : {}),
      ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {})
    }
    const requestOptions = {
      headers: baseHeaders,
      ...(options || {})
    }

    const response = await fetch(basePath, requestOptions)

    const enviouAutenticacao = withCredentials && token !== ''
    if (response.status === 401 && enviouAutenticacao) {
      if (!isRetry) {
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          return fetchData<T>(
            basePath,
            options,
            muteException,
            skipContentType,
            true
          )
        }
      }
      clearToken()
    }

    let payload = {} as T
    try {
      payload = await response.json()
    } catch (err: any) {
      // Se não conseguir fazer parse do JSON, criar payload com informações básicas
      payload = {
        message:
          response.status === 403
            ? 'Acesso negado. Você não tem permissão para realizar esta operação.'
            : 'Erro no servidor. Tente novamente.',
        exception: response.status === 403 ? 'Forbidden' : 'ServerError',
        status: response.status
      } as T
    }

    if (response.ok) {
      return Promise.resolve(payload)
    } else {
      // Garantir que o código de status esteja no payload para tratamento adequado
      if (!(payload as any).status) {
        ;(payload as any).status = response.status
      }
      // Se for 403 e não tiver exception definida, definir como Forbidden
      if (response.status === 403 && !(payload as any).exception) {
        ;(payload as any).exception = 'Forbidden'
      }
      return Promise.reject(payload)
    }
  } catch (err: any) {
    if (!muteException) {
      toastService.showToast(
        err,
        err?.exception === 'UsernameExistsException' ? 'warning' : 'error',
        true
      )
    }
    return Promise.reject(err)
  }
}

function get<T>(
  basePath: string,
  options?: RequestInit,
  muteException?: boolean
): Promise<T> {
  return fetchData<T>(basePath, options, muteException)
}

function update<T, R>(
  basePath: string,
  method: string,
  body?: T,
  options?: RequestInit,
  muteException?: boolean
): Promise<R> {
  const optionsParam = options || {}

  return fetchData<R>(
    basePath,
    {
      ...optionsParam,
      method: method,
      body:
        body instanceof FormData || body instanceof Blob
          ? body
          : JSON.stringify(body)
    },
    muteException
  )
}

function post<T, R>(
  basePath: string,
  body: T,
  options?: RequestInit,
  muteException?: boolean
): Promise<R> {
  return update<T, R>(basePath, 'POST', body, options, muteException)
}

function put<T, R>(
  basePath: string,
  body: T,
  options?: RequestInit,
  muteException?: boolean
): Promise<R> {
  return update<T, R>(basePath, 'PUT', body, options, muteException)
}

function remove<T, R>(
  basePath: string,
  body?: T,
  options?: RequestInit,
  muteException?: boolean
): Promise<R> {
  return update<T, R>(basePath, 'DELETE', body, options, muteException)
}

function patch<T, R>(
  basePath: string,
  body?: T,
  options?: RequestInit,
  muteException?: boolean
): Promise<R> {
  return update<T, R>(basePath, 'PATCH', body, options, muteException)
}

function postMultipart<R>(
  basePath: string,
  formData: FormData,
  muteException?: boolean
): Promise<R> {
  return fetchData<R>(
    basePath,
    { method: 'POST', body: formData },
    muteException,
    true
  )
}

/**
 * GET de um recurso binário, com o mesmo cabeçalho de autenticação das demais chamadas.
 *
 * O `fetchData` sempre tenta interpretar a resposta como JSON, então não serve para um PDF.
 * Existe para que o painel possa exibir o documento gerado pelo backend num `<iframe>` — que
 * não consegue enviar o `Authorization` por conta própria.
 */
async function getBlob(basePath: string, isRetry?: boolean): Promise<Blob> {
  const token = getToken()
  const tenantSlug = getTenantSlug()
  const headers: Record<string, string> = {
    ...(token ? { Authorization: token } : {}),
    ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {})
  }

  const response = await fetch(basePath, { headers })
  if (response.status === 401 && token !== '') {
    if (!isRetry) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        return getBlob(basePath, true)
      }
    }
    clearToken()
  }
  if (!response.ok) {
    return Promise.reject({
      status: response.status,
      message: 'Não foi possível carregar o documento.'
    })
  }
  return response.blob()
}

const httpMethods = {
  get,
  getBlob,
  post,
  put,
  remove,
  patch,
  postMultipart
}

export default httpMethods
